import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import logoFull from '@/assets/logo-full.svg';

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Supabase parses the recovery token in the URL hash and emits PASSWORD_RECOVERY
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setHasRecoverySession(true);
    });

    // Also check existing session in case the event already fired
    supabase.auth.getSession().then(({ data }) => {
      const hashHasRecovery = window.location.hash.includes('type=recovery');
      if (data.session && hashHasRecovery) setHasRecoverySession(true);
      else if (hasRecoverySession === null) {
        // Give Supabase a brief moment to process the hash
        setTimeout(() => {
          setHasRecoverySession((prev) => (prev === null ? !!data.session && hashHasRecovery : prev));
        }, 800);
      }
    });

    return () => sub.subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t('auth.newPassword.tooShort'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('auth.newPassword.mismatch'));
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success(t('auth.newPassword.success'));
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (err: any) {
      toast.error(err?.message || t('auth.newPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  if (hasRecoverySession === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8F9FA] p-4">
        <Card className="w-full max-w-md rounded-2xl shadow-lg border-[#DBE2EA]">
          <CardHeader className="space-y-3 text-center">
            <div className="mx-auto mb-2">
              <img src={logoFull} alt="Velox" className="h-10" />
            </div>
            <CardTitle className="font-heading text-2xl text-[#37699E]">
              {t('auth.newPassword.invalidTokenTitle')}
            </CardTitle>
            <CardDescription className="text-[#5A6B7A]">
              {t('auth.newPassword.invalidToken')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg"
              onClick={() => navigate('/auth')}
            >
              {t('auth.newPassword.backToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8F9FA] p-4">
      <Card className="w-full max-w-md rounded-2xl shadow-lg border-[#DBE2EA]">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto mb-2">
            <img src={logoFull} alt="Velox" className="h-10" />
          </div>
          <CardTitle className="font-heading text-2xl text-[#37699E]">
            {t('auth.newPassword.title')}
          </CardTitle>
          <CardDescription className="text-[#5A6B7A]">
            {t('auth.newPassword.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('auth.newPassword.password')}
              </Label>
              <Input
                id="new-password"
                type="password"
                placeholder={t('auth.newPassword.placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading || hasRecoverySession === null}
                required
                minLength={8}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('auth.newPassword.confirmPassword')}
              </Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder={t('auth.newPassword.placeholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading || hasRecoverySession === null}
                required
                minLength={8}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg"
              disabled={isLoading || hasRecoverySession === null}
            >
              {isLoading ? t('auth.newPassword.loading') : t('auth.newPassword.submit')}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => navigate('/auth')}
              disabled={isLoading}
            >
              {t('auth.newPassword.backToLogin')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
